
import { useEffect } from 'react';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

const RuralAfricaPotentialPage = () => {
  useEffect(() => {
    document.title = 'Rural Africa\'s Tremendous Potential - Investment Analysis | TS Dotyeni International';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Exploring the untapped economic potential of rural African markets and development opportunities. Comprehensive research on rural Africa investment potential from TS Dotyeni International experts.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'rural Africa potential, African development, rural investment, economic opportunities, sustainable development');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-emerald-900 to-emerald-700 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/7722aaabd8f8e0c8c7336f9d6aa87d03.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-emerald-900/70"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Rural Africa's Tremendous Potential
          </h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
            Exploring the untapped economic potential of rural African markets and transformative development opportunities
          </p>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Executive Summary */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Executive Summary</h2>
          <div className="bg-emerald-50 rounded-2xl p-8 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              Rural Africa holds extraordinary untapped potential, with 70% of the continent's population living in rural areas and vast natural resources remaining underutilized. Strategic investment in rural development can unlock trillion-dollar opportunities while creating sustainable prosperity for millions of people.
            </p>
          </div>
        </section>

        {/* The Scale of Opportunity */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Scale of Opportunity</h2>
          
          <div className="mb-8">
            <img 
              src="https://readdy.ai/api/search-image?query=Thriving%20African%20rural%20community%20with%20modern%20amenities%2C%20successful%20agricultural%20projects%2C%20renewable%20energy%20installations%2C%20economic%20growth%20indicators%2C%20bright%20and%20optimistic%20atmosphere%2C%20development%20potential%20visualization&width=800&height=400&seq=potential-001&orientation=landscape"
              alt="Rural Africa development potential"
              className="w-full h-64 object-cover object-top rounded-xl mb-6"
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">870M</div>
              <div className="text-gray-900 font-semibold mb-2">Rural Population</div>
              <div className="text-gray-600">Massive consumer and workforce base</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">60%</div>
              <div className="text-gray-900 font-semibold mb-2">Arable Land</div>
              <div className="text-gray-600">Of world's uncultivated arable land</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">$1.8T</div>
              <div className="text-gray-900 font-semibold mb-2">Market Potential</div>
              <div className="text-gray-600">Rural economy potential by 2030</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">30%</div>
              <div className="text-gray-900 font-semibold mb-2">Global Minerals</div>
              <div className="text-gray-600">Of world's mineral reserves</div>
            </div>
          </div>
        </section>

        {/* Untapped Sectors */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Untapped High-Potential Sectors</h2>
          
          <div className="space-y-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                <i className="ri-plant-line mr-3 text-emerald-600"></i>
                Agriculture & Food Systems
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Africa imports $75 billion worth of food annually despite having 60% of the world's uncultivated arable land. This represents a massive opportunity for agricultural development and food processing industries.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <strong className="text-gray-900">Key Opportunities:</strong>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>Crop production and processing</li>
                    <li>Livestock development</li>
                    <li>Aquaculture and fisheries</li>
                    <li>Agricultural technology</li>
                  </ul>
                </div>
                <div>
                  <strong className="text-gray-900">Investment Potential:</strong>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>$400B market by 2030</li>
                    <li>20-30% annual returns</li>
                    <li>Strong export potential</li>
                    <li>Government support</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                <i className="ri-sun-line mr-3 text-emerald-600"></i>
                Renewable Energy
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Rural Africa has exceptional solar and wind resources, with many areas receiving over 300 days of sunshine annually. Energy access remains limited, creating massive opportunities for renewable energy development.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <strong className="text-gray-900">Resource Advantages:</strong>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>Excellent solar irradiation</li>
                    <li>Strong wind corridors</li>
                    <li>Abundant biomass resources</li>
                    <li>Hydroelectric potential</li>
                  </ul>
                </div>
                <div>
                  <strong className="text-gray-900">Market Opportunity:</strong>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>600M people without electricity</li>
                    <li>$200B investment needed</li>
                    <li>15-25% IRR potential</li>
                    <li>Carbon credit opportunities</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                <i className="ri-smartphone-line mr-3 text-emerald-600"></i>
                Digital Infrastructure & Services
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Mobile penetration in rural Africa is growing rapidly, creating opportunities for digital financial services, e-commerce, and digital agriculture solutions that can transform rural economies.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <strong className="text-gray-900">Growth Drivers:</strong>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>Rapid mobile adoption</li>
                    <li>Young, tech-savvy population</li>
                    <li>Leapfrog opportunities</li>
                    <li>Government digitization</li>
                  </ul>
                </div>
                <div>
                  <strong className="text-gray-900">Service Opportunities:</strong>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>Mobile money and banking</li>
                    <li>Digital agriculture platforms</li>
                    <li>E-commerce and logistics</li>
                    <li>Telemedicine and education</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Transformative Success Stories</h2>
          
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Ethiopia's Agricultural Transformation</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Ethiopia's Agricultural Growth Program invested $3.5 billion in rural development, resulting in 8% annual agricultural growth and lifting 2 million people out of poverty. The program demonstrated how strategic rural investment can create sustainable economic transformation.
              </p>
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-emerald-600">8%</div>
                  <div className="text-gray-600">Annual Growth</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">2M</div>
                  <div className="text-gray-600">People Lifted from Poverty</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">40%</div>
                  <div className="text-gray-600">Increase in Productivity</div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Kenya's M-Pesa Revolution</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                M-Pesa's mobile money platform transformed rural Kenya's economy, enabling financial inclusion for millions and creating a $2 billion digital economy. This demonstrates the transformative potential of digital solutions in rural Africa.
              </p>
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-emerald-600">30M</div>
                  <div className="text-gray-600">Active Users</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">$2B</div>
                  <div className="text-gray-600">Digital Economy Value</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">70%</div>
                  <div className="text-gray-600">Financial Inclusion Rate</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Investment Framework */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Strategic Investment Framework</h2>
          
          <div className="bg-emerald-50 rounded-xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Four Pillars of Rural Africa Investment</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">1. Infrastructure Development</h4>
                <p className="text-gray-600">Transportation, energy, and digital infrastructure that connects rural areas to markets and opportunities.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">2. Value Chain Integration</h4>
                <p className="text-gray-600">Processing, storage, and distribution facilities that add value to rural production and create local employment.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">3. Human Capital Development</h4>
                <p className="text-gray-600">Education, training, and healthcare investments that build local capacity and create sustainable development.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">4. Technology Innovation</h4>
                <p className="text-gray-600">Digital solutions and modern technologies that increase productivity and connect rural communities to global markets.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Future Outlook */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Future Outlook: 2030 Vision</h2>
          
          <div className="bg-white border border-gray-200 rounded-xl p-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              By 2030, rural Africa has the potential to become a $1.8 trillion economy, supporting 1.2 billion people with modern infrastructure, sustainable agriculture, and thriving local industries. This transformation requires $500 billion in strategic investment over the next decade.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-emerald-600 mb-2">$1.8T</div>
                <div className="text-gray-900 font-semibold mb-1">Economy Size</div>
                <div className="text-gray-600">Rural Africa by 2030</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-600 mb-2">200M</div>
                <div className="text-gray-900 font-semibold mb-1">New Jobs</div>
                <div className="text-gray-600">Created by development</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-600 mb-2">80%</div>
                <div className="text-gray-900 font-semibold mb-1">Energy Access</div>
                <div className="text-gray-600">Rural electrification</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-600 mb-2">$500B</div>
                <div className="text-gray-900 font-semibold mb-1">Investment Needed</div>
                <div className="text-gray-600">To unlock potential</div>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusion: The Time is Now</h2>
          <div className="bg-emerald-50 rounded-2xl p-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Rural Africa's tremendous potential is not just an opportunity—it's an imperative for global sustainable development. The convergence of demographic trends, technological advancement, and policy support creates an unprecedented window for transformative investment.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Investors who recognize and act on this potential today will not only achieve exceptional returns but also contribute to one of the most significant economic transformations in human history.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="bg-emerald-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-semibold mb-4">Unlock Rural Africa's Potential</h3>
            <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
              Partner with TS Dotyeni International to access the most promising rural development opportunities across Africa.
            </p>
            <button className="bg-white text-emerald-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-semibold whitespace-nowrap cursor-pointer">
              Explore Investment Opportunities
            </button>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
};

export default RuralAfricaPotentialPage;
